// Plan-Card — eine Abo-Plan-Karte in der /credits-Abo-Sektion.
// Zeigt Plan-Name, Tagline, großen Preis (Monat/Jahr je nach cycle),
// Monats-Credits, Wert-Hinweise (Briefings/Chats), dynamisches Rabatt-Pill,
// Benefit-Checkliste und CTA. Pro-Plan (highlight) wird hervorgehoben.

import { motion } from 'framer-motion';
import { Check, Loader2, RefreshCw } from 'lucide-react';
import { formatPriceEur } from '@/lib/credits-api';
import type { BillingCycle, SubscriptionPlan } from '@/lib/subscriptions-api';
import type { PlanContent } from './plan-content';

/** Credit-Kosten pro Agent-Briefing (für die Wert-Schätzung). */
const AGENT_BRIEFING_COST = 5;
/** Credit-Kosten pro EVE-Chat (für die Wert-Schätzung). */
const CHAT_COST = 2;

interface PlanCardProps {
  plan: SubscriptionPlan;
  content: PlanContent;
  cycle: BillingCycle;
  /** Dynamischer Rabatt pro Credit gegenüber dem teuersten Tier (≥ 0). */
  discountPct: number;
  highlight: boolean;
  inFlight: boolean;
  disabled: boolean;
  index: number;
  onSubscribe: (planId: string) => void;
}

export function PlanCard({
  plan,
  content,
  cycle,
  discountPct,
  highlight,
  inFlight,
  disabled,
  index,
  onSubscribe,
}: PlanCardProps) {
  const priceCents = cycle === 'yearly' ? plan.price_yearly_cents : plan.price_monthly_cents;
  const briefings = Math.round(plan.credits_per_month / AGENT_BRIEFING_COST);
  const chats = Math.round(plan.credits_per_month / CHAT_COST);

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07, type: 'spring', stiffness: 240, damping: 24 }}
      className={`glass-strong relative flex flex-col rounded-2xl border p-6 transition-all duration-200 hover:-translate-y-0.5 ${
        highlight
          ? 'border-amber-500/60 shadow-[0_0_40px_-12px_rgba(245,158,11,0.5)] ring-1 ring-amber-500/30 md:-mt-2 md:mb-2 hover:border-amber-400/70'
          : 'border-slate-700/40 hover:border-slate-600/60'
      }`}
    >
      {highlight ? (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-amber-500 px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest text-neutral-950 shadow-sm">
          Beliebt
        </span>
      ) : null}

      {/* Plan-Name + Tagline */}
      <div className="mb-5">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
          {plan.name}
        </p>
        <p className="mt-1 text-[13px] text-slate-500">{content.tagline}</p>
      </div>

      {/* Preis */}
      <div className="mb-5 flex items-baseline gap-1.5">
        <span className="text-4xl font-bold text-white">{formatPriceEur(priceCents)}</span>
        <span className="text-[13px] text-slate-500">
          / {cycle === 'yearly' ? 'Jahr' : 'Monat'}
        </span>
      </div>

      {/* Credits / Monat + Rabatt-Pill */}
      <div className="mb-4 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-[14px]">
          <RefreshCw className="size-4 shrink-0 text-amber-400" />
          <span>
            <span className="font-bold text-amber-300">
              {plan.credits_per_month.toLocaleString('de-DE')}
            </span>{' '}
            <span className="text-slate-300">Credits / Monat</span>
          </span>
        </span>
        {discountPct > 0 ? (
          <span className="shrink-0 rounded-full border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-300">
            −{discountPct}% pro Credit
          </span>
        ) : null}
      </div>

      {/* Wert-Hinweise */}
      <div className="mb-5 grid grid-cols-2 gap-2 rounded-xl border border-slate-800/80 bg-slate-900/40 p-3 text-[12px] text-slate-400">
        <div>
          <span className="font-semibold text-slate-200">≈ {briefings.toLocaleString('de-DE')}</span>
          <br />
          Agent-Briefings / Monat
        </div>
        <div>
          <span className="font-semibold text-slate-200">≈ {chats.toLocaleString('de-DE')}</span>
          <br />
          Chats / Monat
        </div>
      </div>

      {/* Benefit-Checkliste */}
      <ul className="mb-6 space-y-2.5 text-[13px] text-slate-300">
        {content.checklist.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2.5">
            <Check className="mt-0.5 size-4 shrink-0 text-amber-400" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button
        type="button"
        onClick={() => onSubscribe(plan.id)}
        disabled={disabled}
        className={`mt-auto inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
          highlight
            ? 'bg-amber-500 text-neutral-950 hover:bg-amber-400'
            : 'border border-amber-500/50 text-amber-300 hover:bg-amber-500/10'
        }`}
      >
        {inFlight ? <Loader2 className="size-4 animate-spin" /> : null}
        Abonnieren
      </button>
    </motion.div>
  );
}
